import { useCallback, useRef } from 'react';
import {
  AUTH_BACKUP_CODE_MESSAGE,
  OTP_REMEMBER_DEVICE_LABEL,
  OTP_VERIFICATION_BACK_LABEL,
  OTP_VERIFICATION_LOG_OUT_LABEL,
  OTP_VERIFICATION_NEXT_LABEL,
  OTP_VERIFICATION_NOT_WORKING_LABEL,
  TWO_FA_ORG_MANDATORY_HINT,
} from 'common/constants/auth';
import { Alert, Button, Checkbox, Stack } from 'components/ui';
import type { InputProps } from 'components/ui';
import AuthenticationBackupCodesInput from 'pages/Auth/components/AuthenticationBackupCodesInput';
import AuthenticationInput from 'pages/Auth/components/AuthenticationInput';
import AuthExtraActions from 'pages/Auth/components/AuthExtraActions';
import AuthFormHeader from 'pages/Auth/components/AuthFormHeader';
import AuthPageStack from 'pages/Auth/components/AuthPageStack';
import AuthStatusActionLink from 'pages/Auth/components/AuthStatusActionLink';
import AuthTitleText from 'pages/Auth/components/AuthTitleText';
import {
  ActionButtons,
  otpActionButtonSx,
  RememberDeviceLabel,
  RootPaper,
  Subtitle,
  SupportingMessage,
  Title,
} from 'pages/Auth/components/OTP/OTP.styles';
import { OTP_TEST_IDS } from 'pages/Auth/components/OTP/OTP.testIds';
import { getOtpTitles, isOtpSubmitDisabled } from 'pages/Auth/components/OTP/OTP.utils';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import type { OtpVerificationProps } from './OtpVerification.props';

const getFirstErrorMessage = (value?: string | string[]) => {
  if (!value) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};

const OtpVerification = ({
  formState,
  isCodeWorking,
  isSubmitting = false,
  rememberThisComputer,
  showMandatoryHint,
  showRememberThisComputer,
  showHeader = false,
  showBackupCodeAction = false,
  onCancel,
  onCodeNotWorking,
  onInputBlur,
  onInputChange,
  onRememberThisComputerChange,
  onSubmit,
}: OtpVerificationProps) => {
  const titles = getOtpTitles(isCodeWorking);
  const [titleLead, ...titleTailParts] = titles.title.split(' ');
  const titleTail = titleTailParts.join(' ');
  const alertMessage = getFirstErrorMessage(isCodeWorking ? formState.dbErrors?.backupCode : formState.dbErrors?.otp);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const backupCodeInputRef = useRef<HTMLInputElement>(null);
  const focusOtpField = useCallback(() => {
    if (isCodeWorking) {
      backupCodeInputRef.current?.focus();
      return;
    }

    otpInputRef.current?.focus();
  }, [isCodeWorking]);
  const isSubmitDisabled = isOtpSubmitDisabled(formState, isSubmitting);
  const handleInputKeyDown: NonNullable<InputProps['onKeyDown']> = useCallback(
    event => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      if (!isSubmitDisabled) {
        onSubmit();
      }
    },
    [isSubmitDisabled, onSubmit]
  );

  useAuthInitialFocus(focusOtpField, !isSubmitting);

  return (
    <AuthPageStack>
      {showHeader ? (
        <AuthFormHeader
          subtitle={titles.subtitle}
          title={<AuthTitleText lead={titleLead} tail={titleTail} />}
        />
      ) : null}
      <RootPaper>
        <Stack spacing={4}>
          {!showHeader ? (
            <Stack spacing={2}>
              <Title>
                <AuthTitleText lead={titleLead} tail={titleTail} />
              </Title>
              <Subtitle>{titles.subtitle}</Subtitle>
            </Stack>
          ) : null}
          {alertMessage ? (
            <Alert severity="error" visualVariant="inlineError">
              {alertMessage}
            </Alert>
          ) : null}
          {showMandatoryHint ? (
            <Alert data-testid={OTP_TEST_IDS.mandatory2faNotice} severity="info" visualVariant="inline">
              {TWO_FA_ORG_MANDATORY_HINT}
            </Alert>
          ) : null}
          {!isCodeWorking ? (
            <Stack spacing={1.5}>
              <AuthenticationInput
                disabled={isSubmitting}
                formState={formState}
                handleBlur={onInputBlur}
                handleChange={onInputChange}
                inputRef={otpInputRef}
                onKeyDown={handleInputKeyDown}
              />
              {showRememberThisComputer ? (
                <Checkbox
                  checked={rememberThisComputer}
                  disabled={isSubmitting}
                  id="auth-otp-remember-computer"
                  label={(
                    <RememberDeviceLabel>
                      {OTP_REMEMBER_DEVICE_LABEL}
                    </RememberDeviceLabel>
                  )}
                  onChange={onRememberThisComputerChange}
                />
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <AuthenticationBackupCodesInput
                disabled={isSubmitting}
                formState={formState}
                handleBlur={onInputBlur}
                handleChange={onInputChange}
                inputRef={backupCodeInputRef}
                onKeyDown={handleInputKeyDown}
              />
              <SupportingMessage>{AUTH_BACKUP_CODE_MESSAGE}</SupportingMessage>
            </Stack>
          )}
        </Stack>
        <ActionButtons>
          <Button
            disabled={isSubmitting}
            id="auth-otp-back-button"
            onClick={onCancel}
            variant="text"
            visualVariant="primaryGhost"
          >
            {isCodeWorking ? OTP_VERIFICATION_BACK_LABEL : OTP_VERIFICATION_LOG_OUT_LABEL}
          </Button>
          <Button
            data-testid={OTP_TEST_IDS.verificationNextButton}
            disabled={isSubmitDisabled}
            id="auth-otp-next-button"
            loading={isSubmitting}
            onClick={onSubmit}
            sizeVariant="compact"
            sx={otpActionButtonSx}
            variant="contained"
            visualVariant="positive"
          >
            {OTP_VERIFICATION_NEXT_LABEL}
          </Button>
        </ActionButtons>
      </RootPaper>
      {!isCodeWorking && showBackupCodeAction ? (
        <AuthExtraActions>
          <AuthStatusActionLink
            component="button"
            data-testid={OTP_TEST_IDS.verificationCodeNotWorkingButton}
            disabled={isSubmitting}
            id="auth-otp-code-not-working-button"
            onClick={onCodeNotWorking}
            type="button"
          >
            {OTP_VERIFICATION_NOT_WORKING_LABEL}
          </AuthStatusActionLink>
        </AuthExtraActions>
      ) : null}
    </AuthPageStack>
  );
};

export default OtpVerification;
